import React from 'react';
import styled from 'styled-components';

// ─── HTTP status colour helper ───────────────────────────────────────────────

export function httpStatusChip(code) {
    if (!code) return null;
    const n = parseInt(code, 10);
    if (n >= 200 && n < 300) return { bg: '#dcfce7', color: '#15803d' };
    if (n >= 300 && n < 400) return { bg: '#fef3c7', color: '#92400e' };
    if (n >= 400 && n < 500) return { bg: '#ffedd5', color: '#c2410c' };
    return { bg: '#fee2e2', color: '#dc2626' };
}

function httpStatusTitle(code) {
    const n = parseInt(code, 10);
    if (n === 200) return '200 OK — page loaded successfully';
    if (n === 301) return '301 Moved Permanently — this URL redirects to a new permanent location';
    if (n === 302) return '302 Found — temporary redirect';
    if (n === 307) return '307 Temporary Redirect';
    if (n === 308) return '308 Permanent Redirect';
    if (n === 404) return '404 Not Found — the page does not exist';
    if (n === 410) return '410 Gone — the page has been permanently removed';
    if (n === 403) return '403 Forbidden — access to this page is denied';
    if (n === 500) return '500 Internal Server Error';
    if (n === 503) return '503 Service Unavailable';
    if (n >= 200 && n < 300) return `${n} — success`;
    if (n >= 300 && n < 400) return `${n} — redirect`;
    if (n >= 400 && n < 500) return `${n} — client error`;
    if (n >= 500)             return `${n} — server error`;
    return String(code);
}

// ─── Components ──────────────────────────────────────────────────────────────

/**
 * Pill — coloured semantic status badge.
 * Props: $green | $red | $blue | $amber  (default: grey)
 */
export const Pill = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 9px;
    border-radius: 99px;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.2px;
    white-space: nowrap;
    background: ${({ $green, $red, $blue, $amber }) =>
        $green ? '#dcfce7' : $red ? '#fee2e2' : $blue ? '#dbeafe' : $amber ? '#fef3c7' : '#f1f5f9'};
    color: ${({ $green, $red, $blue, $amber }) =>
        $green ? '#15803d' : $red ? '#dc2626' : $blue ? '#1d4ed8' : $amber ? '#92400e' : '#475569'};
`;

/**
 * IndexablePill — shows ✓ Indexable or ✗ Non-indexable with a consistent tooltip.
 */
export function IndexablePill({ indexable }) {
    if (indexable === true)  return <Pill $green title="This page is included in search engine results">✓ Indexable</Pill>;
    if (indexable === false) return <Pill $red   title="This page is excluded from search engine results — it may have a noindex directive, be blocked by robots.txt, or canonicalized to another URL">✗ Non-indexable</Pill>;
    return null;
}

/**
 * ScopePill — shows Internal or External crawl scope with a consistent tooltip.
 */
export function ScopePill({ crawlScope }) {
    if (crawlScope === 'INTERNAL') return <Pill $blue  title="Internal — this URL falls within the domain and path boundary configured in workspace settings, and will be actively crawled">Internal</Pill>;
    if (crawlScope === 'EXTERNAL') return <Pill $amber title="External — this URL is outside the crawl boundary configured in workspace settings, and will not be actively followed">External</Pill>;
    return null;
}

/**
 * HttpStatusBadge — shows an HTTP status code, auto-coloured by range, with a descriptive tooltip.
 */
export function HttpStatusBadge({ code }) {
    if (!code) return null;
    const { bg, color } = httpStatusChip(code);
    return (
        <BadgeEl style={{ background: bg, color, cursor: 'help' }} title={httpStatusTitle(code)}>
            {code}
        </BadgeEl>
    );
}

export const BadgeEl = styled.span`
    font-size: 0.65rem;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 4px;
    flex-shrink: 0;
    white-space: nowrap;
`;

/**
 * ErrBadge — red badge for crawl failures. Pass crawlStatus as title for the error detail.
 */
export function ErrBadge({ children = 'Crawl failed', title }) {
    return <ErrBadgeEl title={title || 'This page could not be crawled'}>{children}</ErrBadgeEl>;
}

/**
 * LoopBadge — red badge for redirect loops.
 */
export function LoopBadge() {
    return <ErrBadgeEl title="Redirect loop detected — this URL redirects in a cycle">Loop</ErrBadgeEl>;
}

const ErrBadgeEl = styled(BadgeEl)`
    background: #fee2e2;
    color: #dc2626;
    cursor: help;
`;

/**
 * OrphanBadge — amber badge for pages with no inbound links and no sitemap reference.
 */
export const OrphanBadge = styled.span.attrs({
    title: 'This page has no inbound links and is not referenced by any sitemap — it can only be reached by direct URL',
})`
    font-size: 0.65rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 99px;
    background: #fef3c7;
    color: #92400e;
    cursor: help;
    white-space: nowrap;
`;

/**
 * CountBadge — small indigo numeric count badge.
 * $solid=true for filled background (used in tab bar active indicators).
 */
export const CountBadge = styled.span`
    background: ${({ $solid }) => $solid ? '#6366f1' : '#ede9fe'};
    color: ${({ $solid }) => $solid ? '#fff' : '#6366f1'};
    font-size: 0.65rem;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 99px;
    white-space: nowrap;
`;

/**
 * IgnBadge — grey badge shown on fields that have no recommendation set.
 */
export const IgnBadge = styled.span`
    font-size: 0.65rem;
    font-weight: 600;
    color: #94a3b8;
    background: #f1f5f9;
    border-radius: 4px;
    padding: 1px 5px;
    cursor: help;
    align-self: flex-start;
`;
